import type { BaseSyntheticEvent, ReactNode, TransitionEvent } from 'react';
import styled, { keyframes } from 'styled-components';

import { button } from '@/styles/shared';

export const NoWrap = styled.span<{
  $wrap?: boolean;
  children: ReactNode;
}>`
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
`;

export const Title = styled.h1<{
  $compact?: boolean;
}>`
  font-size: ${({ $compact }) => ($compact ? '26px' : '34px')};
  line-height: 1.2;
  margin: 32px 0 24px;

  @media ${({ theme }) => theme.media.tablet} {
    font-size: ${({ $compact }) => ($compact ? '32px' : '44px')};
  }

  @media ${({ theme }) => theme.media.desktop} {
    margin: 0 0 16px;
  }
`;

export const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 32px;
`;

export const ChecklistItem = styled.div<{
  children: ReactNode;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.dark};
`;

export const CheckIcon = styled.span<{
  'aria-hidden': string;
  children: ReactNode;
}>`
  font-size: 16px;
  flex-shrink: 0;
`;

export const FormCard = styled.form<{
  children: ReactNode;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.lightGray};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.dark};
  }

  input {
    width: 100%;
  }
`;

export const ConsentLabel = styled.label<{
  children: ReactNode;
  htmlFor: string;
}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.gray};

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }

  a {
    color: ${({ theme }) => theme.palette.primary};
  }
`;

export const HoneypotWrapper = styled.span<{
  'aria-hidden': string;
  children: ReactNode;
}>`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

export const SubmitButton = styled.button<{
  children: ReactNode;
  disabled?: boolean;
  type: string;
}>`
  ${button};
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ErrorMessage = styled.span<{
  children: ReactNode;
}>`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.primary};
`;

export const CollapseWrapper = styled.div<{
  $open: boolean;
  children: ReactNode;
  onTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void;
}>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: grid-template-rows 0.4s ease, opacity 0.3s ease;
`;

export const CollapseInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ConfirmationMessage = styled.p<{
  children: ReactNode;
}>`
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.dark};
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.lightGray};
  border-radius: 12px;
  padding: 8px 16px;
  text-align: center;
  animation: ${fadeSlideIn} 0.45s ease forwards;
`;

export const PrivacyNote = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.gray};
  text-align: center;
  margin-top: 12px;
`;
